import { useCallback, useEffect, useRef } from 'react'
import Editor, { type OnMount } from '@monaco-editor/react'
import { useReactFlow } from '@xyflow/react'
import { useGraphStore } from '../stores/useGraphStore'
import { pathAtOffset, nearestNodeMatch } from '../lib/cursorPath'
import { pathToId } from '../lib/jsonToGraph'

export function JsonPane() {
  const jsonText = useGraphStore((s) => s.jsonText)
  const parseError = useGraphStore((s) => s.parseError)
  const lastEditedBy = useGraphStore((s) => s.lastEditedBy)
  const docId = useGraphStore((s) => s.docId)
  const readOnly = useGraphStore((s) => s.readOnly)
  const setJsonText = useGraphStore((s) => s.setJsonText)
  const relayout = useGraphStore((s) => s.relayout)

  const flow = useReactFlow()

  const editorRef = useRef<Parameters<OnMount>[0] | null>(null)
  const debounceRef = useRef<number | null>(null)
  const focusDebounceRef = useRef<number | null>(null)

  const handleChange = useCallback(
    (value: string | undefined) => {
      if (readOnly) return
      const text = value ?? ''
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current)
      debounceRef.current = window.setTimeout(() => {
        setJsonText(text)
      }, 300)
    },
    [setJsonText, readOnly],
  )

  const focusNodeAtCursor = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    const model = editor.getModel()
    const pos = editor.getPosition()
    if (!model || !pos) return
    const offset = model.getOffsetAt(pos)
    const text = model.getValue()
    const path = pathAtOffset(text, offset)
    const nodeIds = new Set(useGraphStore.getState().nodes.map((n) => n.id))
    const match = nearestNodeMatch(path, nodeIds, pathToId)

    // Always clear any prior row-level highlight; we'll re-apply below if matched.
    document.querySelectorAll('.graph-row-focused').forEach((el) => {
      el.classList.remove('graph-row-focused')
    })

    if (!match) return
    const { id: targetId, fieldKey } = match
    const node = flow.getNode(targetId)
    if (!node) return
    const width = node.measured?.width ?? 440
    const height = node.measured?.height ?? 200
    flow.setCenter(node.position.x + width / 2, node.position.y + height / 2, {
      zoom: 1.3,
      duration: 350,
    })
    // Persistent selection state for the matching node
    flow.setNodes((ns) =>
      ns.map((n) => (n.selected === (n.id === targetId) ? n : { ...n, selected: n.id === targetId })),
    )
    // One-shot pulse + persistent row highlight
    requestAnimationFrame(() => {
      const nodeEl = document.querySelector<HTMLElement>(
        `.react-flow__node[data-id="${CSS.escape(targetId)}"]`,
      )
      if (!nodeEl) return
      nodeEl.classList.remove('graph-focus-ping')
      void nodeEl.offsetWidth
      nodeEl.classList.add('graph-focus-ping')

      if (fieldKey !== null) {
        const rowEl = nodeEl.querySelector<HTMLElement>(
          `[data-field-key="${CSS.escape(fieldKey)}"]`,
        )
        if (rowEl) {
          rowEl.classList.remove('graph-row-ping')
          void rowEl.offsetWidth
          rowEl.classList.add('graph-row-ping')
          rowEl.classList.add('graph-row-focused')
        }
      }
    })
  }, [flow])

  // Sync editor content when canvas is the source of truth.
  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return
    if (lastEditedBy !== 'canvas') return
    const current = editor.getValue()
    if (current !== jsonText) editor.setValue(jsonText)
  }, [jsonText, lastEditedBy])

  return (
    <div className="h-full w-full flex flex-col bg-background border-r border-border">
      <div className="flex items-center justify-between px-4 h-10 border-b border-border flex-shrink-0">
        <div
          className="text-[10px] uppercase tracking-wider text-muted-foreground font-light"
          style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
        >
          JSON
        </div>
        <div className="flex items-center gap-3">
          {parseError ? (
            <span className="text-[11px] text-destructive font-light">invalid</span>
          ) : null}
          <button
            className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground font-light"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
            onClick={relayout}
          >
            Re-layout
          </button>
          {!readOnly && (
            <button
              className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground font-light"
              style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
              onClick={() => {
                const e = editorRef.current
                if (!e) return
                try {
                  const parsed = JSON.parse(e.getValue())
                  const formatted = JSON.stringify(parsed, null, 2)
                  e.setValue(formatted)
                  setJsonText(formatted)
                } catch {
                  /* ignore */
                }
              }}
            >
              Clean
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <Editor
          key={docId ?? 'empty'}
          height="100%"
          defaultLanguage="jsonc"
          defaultValue={jsonText}
          onChange={handleChange}
          onMount={(editor) => {
            editorRef.current = editor
            editor.onDidChangeCursorPosition(() => {
              // Ignore programmatic cursor placements (including the one
              // Monaco does at position 1,1 on first mount). Only react when
              // the user has focused the editor.
              if (!editor.hasTextFocus()) return
              if (focusDebounceRef.current !== null)
                window.clearTimeout(focusDebounceRef.current)
              focusDebounceRef.current = window.setTimeout(focusNodeAtCursor, 120)
            })
            editor.onDidBlurEditorText(() => {
              document.querySelectorAll('.graph-row-focused').forEach((el) => {
                el.classList.remove('graph-row-focused')
              })
            })
          }}
          theme="vs"
          options={{
            readOnly,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            lineNumbers: 'on',
            tabSize: 2,
            wordWrap: 'on',
            renderLineHighlight: 'none',
            scrollbar: { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            overviewRulerBorder: false,
          }}
        />
      </div>
      {parseError && (
        <div className="flex-shrink-0 px-4 py-2 border-t border-border bg-destructive/5 text-[11px] text-destructive font-light truncate">
          {parseError}
        </div>
      )}
    </div>
  )
}
